/**
 * Viewport surface raycast — module-level callback bridge.
 *
 * The viewport registers a scene-backed raycast implementation that resolves
 * hits against the real rendered meshes. Live snapping and projection code can
 * query it without rebuilding temporary proxy geometry on every pointer move.
 */

import type { Vec3 } from "./faceBasis.ts";
import type { PolygonTopology } from "./types.ts";

export interface ViewportSurfaceMeshData {
  topology: PolygonTopology;
  positions: Float32Array;
  worldMatrix: readonly number[];
}

export interface ViewportSurfaceHit {
  objectId: string;
  worldPosition: Vec3;
  normal: Vec3;
  face?: {
    index: number;
    vertices: readonly Vec3[];
    mesh?: ViewportSurfaceMeshData;
  };
  triangle?: {
    vertices: readonly [Vec3, Vec3, Vec3];
  };
}

type ViewportSurfaceRaycastFn = (
  ndcX: number,
  ndcY: number,
  excludeObjectIds?: ReadonlySet<string>,
) => ViewportSurfaceHit | null;

let _fn: ViewportSurfaceRaycastFn | null = null;

const EMPTY_EXCLUDE_IDS = new Set<string>();

/** Called by the viewport to provide (or remove) the live scene raycast implementation. */
export function registerViewportSurfaceRaycast(
  fn: ViewportSurfaceRaycastFn | null,
): void {
  _fn = fn;
}

/** Query the rendered viewport surface under the given NDC position. */
export function queryViewportSurface(
  ndcX: number,
  ndcY: number,
  excludeObjectIds: ReadonlySet<string> = EMPTY_EXCLUDE_IDS,
): ViewportSurfaceHit | null {
  return _fn?.(ndcX, ndcY, excludeObjectIds) ?? null;
}
